/**
 * QE2-CORRIDOR — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 5 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_QE2_CORRIDOR: Location[] = [
// ───────────────────────────────────────────────────────────── CARSTAIRS ──
  {
    slug: "carstairs",
    name: "Carstairs",
    region: "qe2-corridor",
    city: "Carstairs",
    province: "Alberta",
    coordinates: { lat: 51.5667, lng: -114.1 },
    tier: 3,
    driveMinutesFromCalgary: 55,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Olds-Didsbury aerodrome",
      distanceToAerodromeKm: 16,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 16 km from Olds-Didsbury aerodrome, Carstairs lies outside the charted Calgary-area control zones, and no authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The grain quarters around Carstairs run one calendar in unison — green, gold, swath, stubble, snow — while the fairways of the Didsbury-Carstairs Golf Club hold a single green from thaw to freeze-up.",
      consequence:
        "The week a shoot flies decides what the town's setting says: the same property reads as working farmland at harvest and as bare plain after melt, and once the district moves on, that frame is gone until next year.",
      resolution:
        "Bookings here are placed against the crop calendar as much as the diary, and where the point is the change itself, repeat passes build a progress series — each flight delivered in the standard turnaround.",
    },
    landmarks: [
      "Hwy 2A",
      "Carstairs Sportsplex",
      "Carstairs Memorial Arena",
      "Carstairs & District Museum",
      "Didsbury-Carstairs Golf Club",
    ],
    terrain:
      "Level to gently rolling grain country on the corridor plain — quarter sections running unbroken to every horizon, with the town grid and its shelter trees the only vertical interruption in the frame.",
    windNote:
      "Chinook belt, and open. West wind crosses the level country around Carstairs with nothing to slow it; on a chinook day the difference between the average and the gusts is what decides whether the flight happens.",
    seasonalWindow:
      "Early-summer evenings rake long shadows across the field texture around town; harvest brings the strongest colour of the year, and the first lasting snow closes the palette to white and grey.",
    bylawNote:
      "Town of Carstairs rules govern launch and landing in its parks and public spaces; the working default is takeoff from private property with the owner's consent.",
    subjectMix: ["agricultural", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 55 minutes north of the Calgary base. An easy half-day booking, and Crossfield or Didsbury sit on the same run and can share the mobilisation.",
    faqs: [
      {
        question: "Do I need any airspace approval for a drone shoot in Carstairs?",
        answer:
          "No. Carstairs sits in uncontrolled Class G airspace, outside the charted Calgary-area control zones and about 16 km from Olds-Didsbury aerodrome, so no airspace authorisation is normally required. The specific site and altitude are still confirmed against NAV Drone before every flight, as they are for every booking.",
      },
      {
        question: "When in the year do the fields around Carstairs photograph best?",
        answer:
          "In full crop or under fresh snow. The quarters around town move through green, gold and stubble within a few weeks each, and the frame changes character with them. The fairways at Didsbury-Carstairs Golf Club hold their green from thaw to freeze-up — the most stable colour in the district.",
      },
      {
        question: "Can community facilities like the Carstairs Sportsplex be photographed from the air?",
        answer:
          "Yes, subject to the usual ground rules. The Sportsplex, the Carstairs Memorial Arena and the Carstairs & District Museum are legitimate aerial subjects in Class G air; what matters is where the aircraft launches. Staging is arranged from private property with consent, and the flight plan keeps clear of people below.",
      },
    ],
    primaryKeywords: [
      "drone photography Carstairs",
      "aerial photography Carstairs",
      "Carstairs drone services",
    ],
    nearestLocations: ["crossfield", "didsbury", "cremona"],
  },

  // ──────────────────────────────────────────────────────────── CROSSFIELD ──
  {
    slug: "crossfield",
    name: "Crossfield",
    region: "qe2-corridor",
    city: "Crossfield",
    province: "Alberta",
    coordinates: { lat: 51.4167, lng: -114.0167 },
    tier: 3,
    driveMinutesFromCalgary: 40,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 18,
      authorizationRequired: false,
      authorizationNote:
        "Crossfield lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 18 km from Airdrie aerodrome. No airspace authorisation is normally required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Crossfield stands on open corridor ground with a clear fetch west to the foothills; the shallow Nose Creek line beside town is the only fold in the land, and it is not deep enough to slow a chinook.",
      consequence:
        "A forecast that reads flyable from a sheltered Calgary yard can be at gust limits by the time it reaches 1 St W, and a shoot booked without regard to the wind loses its window on site.",
      resolution:
        "Crossfield bookings are set into the calm end of the day — usually first light, before the chinook builds — with the gust spread checked against the airframe's limits before mobilisation, and delivered in the standard turnaround.",
    },
    landmarks: [
      "1 St W",
      "Railway Ave",
      "Nose Creek",
      "Crossfield Golf Club",
      "Crossfield Agriplex",
    ],
    terrain:
      "Open corridor prairie on the survey grid, flat enough that grain bins and the Crossfield Agriplex read as skyline, with the shallow upper reach of Nose Creek threading past town.",
    windNote:
      "Chinook belt on open ground. Nose Creek is too shallow to shelter anything, so wind arrives at Crossfield at full strength; the calm hours of a chinook day are usually the early ones.",
    seasonalWindow:
      "Green-up shows first along the Nose Creek line, then across the open quarters; late summer carries the corridor's wildfire-smoke risk to contrast, and winter turns the grid white almost overnight.",
    bylawNote:
      "Launches from public land fall under Town of Crossfield rules for parks and pathways; staging from the client's own property with consent is the standard approach.",
    subjectMix: ["agricultural", "industrial-yard", "commercial-retail"],
    mobilisationNote:
      "Roughly 40 minutes north of the Calgary base — close enough for a same-day booking with a morning confirmation. Airdrie and Carstairs bracket it on the same run.",
    faqs: [
      {
        question: "Does flying a drone in Crossfield require NAV CANADA authorisation?",
        answer:
          "Normally, no. Crossfield lies in uncontrolled Class G airspace, outside the charted Calgary-area control zones and about 18 km from Airdrie aerodrome, so no airspace authorisation is required for routine work. The exact site and altitude are still checked against NAV Drone before every flight is confirmed.",
      },
      {
        question: "How does wind affect scheduling a drone shoot in Crossfield?",
        answer:
          "More than most clients expect. The town stands on open corridor ground where a chinook arrives at full strength, and the shallow Nose Creek line offers no real shelter. Bookings are set into the calm end of the forecast — usually morning — with a fallback window held against the gusts.",
      },
    ],
    primaryKeywords: [
      "drone photography Crossfield",
      "aerial photography Crossfield",
      "Crossfield drone services",
    ],
    nearestLocations: ["airdrie-downtown", "carstairs", "cremona"],
  },

  // ────────────────────────────────────────────────────────────── DIDSBURY ──
  {
    slug: "didsbury",
    name: "Didsbury",
    region: "qe2-corridor",
    city: "Didsbury",
    province: "Alberta",
    coordinates: { lat: 51.65, lng: -114.1333 },
    tier: 3,
    driveMinutesFromCalgary: 60,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Olds-Didsbury aerodrome",
      distanceToAerodromeKm: 7,
      authorizationRequired: false,
      authorizationNote:
        "Didsbury sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 7 km from Olds-Didsbury aerodrome. Authorisation is not normally required here. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Didsbury's horizon is level in every direction, so evening light here runs its full length to true sunset — and it is only in that last low hour that the shallow Rosebud River valley registers as relief at all.",
      consequence:
        "Flown at midday, the country around town photographs as a plain and the river line all but disappears; the frames come back flat, with nothing separating one quarter from the next.",
      resolution:
        "A golden-hour hold is flown against the low light, when the Rosebud valley and the roll of the land carry shadow — the set delivered in the standard turnaround.",
    },
    landmarks: [
      "20 St",
      "Hwy 582",
      "Rosebud River",
      "Didsbury Golf & Curling Club",
      "Didsbury Museum",
    ],
    terrain:
      "Gently rolling grain and hay country, with the shallow valley of the Rosebud River winding through the district — low relief that only reads from the air when the light is low.",
    windNote:
      "Chinook belt. The Rosebud River valley is too slight to funnel or shelter the wind, so forecasts for the open prairie translate directly to conditions over town.",
    seasonalWindow:
      "Summer evenings run long over the flat country and rake the Rosebud valley into relief; in winter the sun stays low enough to do the same work through much of the short day, snow permitting.",
    bylawNote:
      "Town of Didsbury park and open-space rules apply to launches from municipal land; takeoff and landing from private property with the owner's permission is the default.",
    subjectMix: ["agricultural", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About an hour north of the Calgary base. Best treated as a half-day; Carstairs and Olds sit on the same corridor and can share the trip.",
    faqs: [
      {
        question: "Does the Olds-Didsbury aerodrome limit drone flights over Didsbury?",
        answer:
          "Not in the ordinary case. Didsbury sits in uncontrolled Class G airspace, outside the charted Calgary-area control zones, with the aerodrome about 7 km away — close, but no airspace authorisation is normally required. Because it is close, the specific site and altitude are confirmed against NAV Drone before every flight.",
      },
      {
        question: "What is the best light for aerial photography in Didsbury?",
        answer:
          "The low hours. Didsbury's horizon is flat in every direction, so evening light runs to true sunset — and it is only at those low angles that the shallow Rosebud River valley shows as relief. Midday flattens the country; the last hour puts the roll back into it.",
      },
      {
        question: "Can one trip cover both Didsbury and Olds?",
        answer:
          "Yes. The two towns share the same corridor north of Calgary, with Carstairs on the same line, so two or three sites can share one mobilisation. The practical booking is a half-day window, with the order of sites set by the light rather than the map.",
      },
    ],
    primaryKeywords: [
      "drone photography Didsbury",
      "aerial photography Didsbury",
      "Didsbury drone services",
    ],
    nearestLocations: ["carstairs", "olds", "crossfield"],
  },

  // ────────────────────────────────────────────────────────────────── OLDS ──
  {
    slug: "olds",
    name: "Olds",
    region: "qe2-corridor",
    city: "Olds",
    province: "Alberta",
    coordinates: { lat: 51.7917, lng: -114.1064 },
    tier: 3,
    driveMinutesFromCalgary: 70,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Olds-Didsbury aerodrome",
      distanceToAerodromeKm: 9,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 9 km from Olds-Didsbury aerodrome, Olds lies outside the charted Calgary-area control zones, and no authorisation is normally required. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Olds is a dry-grid town from the air: no river bends through it and no lake borders it, and after the melt the only water in most frames is what the season leaves standing in dugouts.",
      consequence:
        "A composition that waits for a waterline to organise it never finds one here — without a deliberate anchor, an Olds property floats in undifferentiated grain country.",
      resolution:
        "The orbit is built on the geometry the town does have — Hwy 2A, the quarter lines and the patchwork of small fields around Olds College — a boundary-line orbit that locates the property precisely, delivered in the standard turnaround.",
    },
    landmarks: [
      "Hwy 2A",
      "50 Ave",
      "College Ave",
      "Olds College",
      "Olds Sportsplex",
    ],
    terrain:
      "Dry, open grain country on the corridor plain — a large town grid set among full quarter sections, with the small trial fields around Olds College breaking the pattern at its edge.",
    windNote:
      "Chinook belt. Open ground in every direction means no terrain shelters an Olds site, and none accelerates the wind either — the regional forecast is the site forecast.",
    seasonalWindow:
      "Spring melt leaves the year's only standing water in dugouts and field corners; the small fields around Olds College change stage more often than the grain quarters, so the campus edge of town carries the most seasonal variety.",
    bylawNote:
      "Town of Olds rules govern municipal parks and public grounds as launch sites; flights stage from private property with the owner's consent as standard.",
    subjectMix: ["agricultural", "commercial-retail", "municipal-infrastructure", "industrial-yard"],
    mobilisationNote:
      "About 70 minutes north of the Calgary base. A half-day booking in practice, and Didsbury or Carstairs on the way up can share the mobilisation.",
    faqs: [
      {
        question: "What airspace applies to drone photography in Olds?",
        answer:
          "Uncontrolled Class G. Olds sits outside the charted Calgary-area control zones, about 9 km from Olds-Didsbury aerodrome, and no airspace authorisation is normally required for work here. As standard practice, the specific site and altitude are confirmed against NAV Drone before every flight is flown.",
      },
      {
        question: "With no river or lake at Olds, what anchors an aerial composition?",
        answer:
          "The grid, and the college. Olds reads from the air as roads, quarter lines and rooflines, so orbits are planned to hold Hwy 2A or a named edge of town in frame, and the patchwork of small fields around Olds College gives the town a feature no neighbour on the corridor can show.",
      },
    ],
    primaryKeywords: [
      "drone photography Olds",
      "aerial photography Olds",
      "Olds drone services",
    ],
    nearestLocations: ["didsbury", "sundre", "carstairs"],
  },

  // ──────────────────────────────────────────────────────────────── SUNDRE ──
  {
    slug: "sundre",
    name: "Sundre",
    region: "qe2-corridor",
    city: "Sundre",
    province: "Alberta",
    coordinates: { lat: 51.7958, lng: -114.6414 },
    tier: 4,
    driveMinutesFromCalgary: 80,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Sundre aerodrome",
      distanceToAerodromeKm: 4,
      authorizationRequired: false,
      authorizationNote:
        "Sundre lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 4 km from Sundre aerodrome. No airspace authorisation is normally required. Sundre aerodrome runs local circuit traffic nearby, so flights are planned with its procedures in mind. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Sundre's airspace file reads calm — Class G, uncontrolled — but its one number is the tightest on the corridor: the Sundre aerodrome sits about 4 km from Main Ave, close enough that circuit traffic works the same sky a drone does.",
      consequence:
        "An operator who treats uncontrolled as empty plans an orbit under a live circuit; no authorisation exists to file the problem away, because none is required — it is a lookout and positioning problem instead.",
      resolution:
        "The flight plan is set against the aerodrome's circuit before launch, the site confirmed on NAV Drone, and the orbit placed clear of the approach path — delivered in the standard turnaround.",
    },
    landmarks: [
      "Main Ave",
      "Hwy 27",
      "Red Deer River",
      "Snake Hill Golf Course",
      "Sundre Museum",
    ],
    terrain:
      "The seam between corridor farmland and the foothills — the Red Deer River comes off the mountains at Sundre, treed hill country rising west of town and open agricultural land running east.",
    windNote:
      "Chinook belt at the foothill edge. West wind runs down the Red Deer River valley with its gusts already built in, though the treed hills take some edge off surface wind compared with the open country east.",
    seasonalWindow:
      "Snow holds in the treed hills west of town after the farmland east has cleared; autumn briefly turns the stands along the Red Deer River gold, and spring runoff gives the river its strongest presence in the frame.",
    bylawNote:
      "Town of Sundre park and pathway rules govern takeoff and landing on municipal land; the default is to stage from the client's own property with consent.",
    subjectMix: ["agricultural", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 80 minutes northwest of the Calgary base. Planned as a half-day at minimum; Olds and Didsbury sit on the route back and can extend the same trip.",
    faqs: [
      {
        question: "Does the Sundre aerodrome restrict drone flights in town?",
        answer:
          "It shapes them rather than restricts them. Sundre's airspace is uncontrolled Class G and no authorisation is normally required, but the aerodrome sits about 4 km out, so flights are planned with its circuit traffic in mind and the site is confirmed against NAV Drone before every launch.",
      },
      {
        question: "What does an aerial frame show at Sundre that ground photography cannot?",
        answer:
          "The meeting point. Sundre is where the Red Deer River leaves the foothills, with treed hill country west and open farmland east, and only altitude puts all three in one frame. A property's position in that transition — river side, hill side or open side — is its story.",
      },
      {
        question: "When does the Red Deer River photograph best from the air?",
        answer:
          "When it is running full or wearing colour. Spring runoff gives the river its widest, brightest presence in the frame, and autumn turns the deciduous stands along its banks gold for a short window. Midsummer works too, though the water sits lower between its gravel bars.",
      },
    ],
    primaryKeywords: [
      "drone photography Sundre",
      "aerial photography Sundre",
      "Sundre drone services",
    ],
    nearestLocations: ["olds", "didsbury", "water-valley"],
  }
];
